import { useId } from "react";

import type { AeroLiftingLineSolveResponse, AeroVector3 } from "../../api/aeroWorkbench";
import type { WingDefinition } from "./api";
import { aircraftSectionModelPresentation } from "./sectionArchitecture";

function maximumDownwash(response: AeroLiftingLineSolveResponse): number {
  const liftAxis: AeroVector3 = response.result.lift_axis_body;
  return Math.max(0, ...response.result.span_loads.map((load) => Math.abs(
    load.induced_velocity_mps[0] * liftAxis[0]
    + load.induced_velocity_mps[1] * liftAxis[1]
    + load.induced_velocity_mps[2] * liftAxis[2]
  )));
}

export function QuickLiftingLineSummary({
  wing,
  response,
  condition,
  status = "current",
}: {
  wing: WingDefinition;
  response: AeroLiftingLineSolveResponse;
  condition: { alphaDeg: number; speedMps: number };
  status?: "current" | "updating" | "warning" | "stale";
}) {
  const titleId = useId();
  const extrapolatedLoads = response.result.span_loads.filter(
    (load) => load.prediction?.metadata?.outside_training_domain === true,
  );
  const maximumExtrapolation = Math.max(
    0,
    ...extrapolatedLoads.map((load) => {
      const value = load.prediction?.metadata?.extrapolation_distance;
      return typeof value === "number" && Number.isFinite(value) ? value : 0;
    }),
  );
  const unconvergedLoads = response.result.span_loads.filter((load) => !load.section_converged);
  const failureClassifierUnavailable = response.result.escalation?.two_d_failure_classifier_available === false;
  const downwash = maximumDownwash(response);
  const modelPresentation = aircraftSectionModelPresentation(response.section_model);
  const fitCacheLabel = typeof response.section_model.fit_cache_hit === "boolean"
    ? response.section_model.fit_cache_hit ? "model fit reused" : "model fitted"
    : null;
  const convergenceLabel = status === "updating"
    ? "updating…"
    : status === "stale"
      ? "stale · update failed"
      : status === "warning"
        ? "result requires review"
        : response.wake_model.converged && response.result.converged
          ? `${response.wake_model.iteration_count} wake iterations`
          : "wake not converged";

  return (
    <section
      className={`quick-ll-summary${status === "updating" ? " is-updating" : ""}${status === "warning" || failureClassifierUnavailable ? " is-warning" : ""}${status === "stale" ? " is-stale" : ""}`}
      data-testid="quick-lifting-line-result"
      data-result-state={status}
      data-wing-station-count={wing.stations.length}
      data-alpha-deg={condition.alphaDeg}
      data-speed-mps={condition.speedMps}
      aria-labelledby={titleId}
      aria-busy={status === "updating"}
    >
      <header>
        <span>
          <strong id={titleId}>Relaxed free wake</strong>
          <small>{modelPresentation.label} · α {condition.alphaDeg.toFixed(1)}° · U {condition.speedMps.toFixed(0)} m/s</small>
        </span>
        <em>{convergenceLabel}</em>
      </header>

      {extrapolatedLoads.length ? (
        <p className="quick-ll-summary-health" role="status">
          {extrapolatedLoads.length} {extrapolatedLoads.length === 1 ? "panel" : "panels"} outside aircraft section model domain
          {maximumExtrapolation > 0 ? ` · distance ${maximumExtrapolation.toFixed(2)}` : ""}
        </p>
      ) : unconvergedLoads.length ? (
        <p className="quick-ll-summary-health" role="status">
          {unconvergedLoads.length} unconverged section {unconvergedLoads.length === 1 ? "closure" : "closures"}
        </p>
      ) : failureClassifierUnavailable ? (
        <p className="quick-ll-summary-health" role="status">
          2D failure risk is not classified · exact section checks are required before certification
        </p>
      ) : null}

      <dl className="quick-ll-summary-metrics">
        <div><dt>C<sub>L</sub></dt><dd>{response.result.lift_coefficient.toFixed(3)}</dd></div>
        <div><dt>C<sub>Di</sub></dt><dd>{response.result.cd_induced.toFixed(4)}</dd></div>
        <div><dt>C<sub>Dp</sub></dt><dd>{response.result.cd_profile.toFixed(4)}</dd></div>
        <div><dt>Lift</dt><dd>{response.result.total_lift_n.toLocaleString(undefined, { maximumFractionDigits: 0 })} N</dd></div>
        <div><dt>max |w|</dt><dd>{downwash.toFixed(2)} m/s</dd></div>
        <div><dt>wake Δx</dt><dd>{response.wake_model.position_residual_m.toExponential(1)} m</dd></div>
      </dl>

      {status === "stale" ? <p className="quick-ll-summary-stale">Displayed wake is the last valid result</p> : null}
      <footer>
        <span><i className="is-positive" /> +Γ</span>
        <span><i className="is-negative" /> −Γ</span>
        <small>
          {response.wake_filaments.length} panels · {response.wake_model.node_count_per_leg} nodes/leg
          {fitCacheLabel ? ` · ${fitCacheLabel}` : ""}
        </small>
      </footer>
    </section>
  );
}
